import * as React from 'react';
import { Input } from '@/components/ui/input';
import type { InputProps } from '@/components/ui/input';
import { cn } from '@/lib/cn';

export interface GlowInputProps extends InputProps {
  containerClassName?: string;
  icon?: React.ReactNode;
}

export const GlowInput = React.forwardRef<HTMLInputElement, GlowInputProps>(
  ({ containerClassName, className, label, error, hint, id, icon, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-foreground mb-1"
          >
            {label}
          </label>
        )}
        <div
          className={cn(
            'auth-glow-wrapper relative group rounded-lg flex items-center justify-center',
            containerClassName,
          )}
        >
          <div className="auth-glow-layers">
            <GlowLayers />
          </div>
          {icon && (
            <span
              aria-hidden
              className="pointer-events-none absolute left-5 top-1/2 z-20 -translate-y-1/2 text-muted-foreground [&>svg]:h-5 [&>svg]:w-5"
            >
              {icon}
            </span>
          )}
          <Input
            ref={ref}
            id={inputId}
            className={cn(
              'relative z-10 h-[56px] rounded-lg border-none bg-[hsl(var(--auth-input-bg))] px-5 text-base text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0',
              icon && 'pl-14',
              className,
            )}
            {...props}
          />
        </div>
        {error && (
          <p className="mt-1 text-sm text-status-error-fg">{error}</p>
        )}
        {hint && !error && (
          <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
        )}
      </div>
    );
  },
);
GlowInput.displayName = 'GlowInput';

/**
 * Five-layer conic-gradient halo, ported from the 21st.dev "animated glowing
 * search bar" by thanh. Each layer is an overflow-clipped wrapper sized
 * slightly larger than the input, holding an oversized rotating conic
 * gradient — the clipping mask leaves only a thin glowing slice around the
 * input edge.
 *
 * Each layer has two bright zones ~180° apart so both sides of the input
 * glow simultaneously, and rotates +180° on focus so each edge point only
 * flashes once during the sweep (the bands end up swapped).
 *
 * Colors come from `--auth-glow-*` tokens so the palette tracks the theme,
 * and the parent `.auth-glow-layers` wrapper applies a slow `hue-rotate`
 * cycle that stays in sync with the GradientDots background.
 *
 * Radius props let callers re-use this halo around inputs with different
 * corner radii — the inner layer matches the wrapped element exactly, and
 * the two outer rings are sized 1–2px larger to match their `-inset` offset.
 */
export interface GlowLayersProps {
  /** Matches the `-inset-[2px]` outer ring (default `rounded-[10px]` for an `rounded-lg` input). */
  outerRadiusClass?: string;
  /** Matches the `-inset-[1px]` mid rings (default `rounded-[9px]` for an `rounded-lg` input). */
  midRadiusClass?: string;
  /** Matches the wrapped element's own corner radius (default `rounded-lg`). */
  innerRadiusClass?: string;
}

export function GlowLayers({
  outerRadiusClass = 'rounded-[10px]',
  midRadiusClass = 'rounded-[9px]',
  innerRadiusClass = 'rounded-lg',
}: GlowLayersProps = {}) {
  const deep = 'hsl(var(--auth-glow-deep))';
  const bright = 'hsl(var(--auth-glow-bright))';
  const soft = 'hsl(var(--auth-glow-soft))';
  const highlight = 'hsl(var(--auth-glow-highlight))';
  return (
    <>
      {/* L1 — outer bloom: deep + bright on opposite sides */}
      <div
        aria-hidden
        className={cn(
          'absolute -inset-[2px] overflow-hidden blur-[2px] pointer-events-none',
          outerRadiusClass,
        )}
      >
        <div
          className="absolute top-1/2 left-1/2 h-[999px] w-[999px] -translate-x-1/2 -translate-y-1/2 rotate-[60deg] auth-glow-layer group-focus-within:rotate-[240deg]"
          style={{
            background: `conic-gradient(transparent, ${deep} 8%, transparent 32%, transparent 52%, ${bright} 60%, transparent 80%)`,
          }}
        />
      </div>

      {/* L2/L3/L4 — three stacked mid layers */}
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          aria-hidden
          className={cn(
            'absolute -inset-[1px] overflow-hidden blur-[2px] pointer-events-none',
            midRadiusClass,
          )}
        >
          <div
            className="absolute top-1/2 left-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rotate-[82deg] auth-glow-layer group-focus-within:rotate-[262deg]"
            style={{
              background: `conic-gradient(transparent, ${deep} 4%, transparent 14%, transparent 50%, ${bright} 56%, transparent 64%)`,
              opacity: 0.55,
            }}
          />
        </div>
      ))}

      {/* L5 — pastel highlight */}
      <div
        aria-hidden
        className={cn(
          'absolute inset-0 overflow-hidden blur-[1px] pointer-events-none',
          innerRadiusClass,
        )}
      >
        <div
          className="absolute top-1/2 left-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rotate-[83deg] auth-glow-layer group-focus-within:rotate-[263deg]"
          style={{
            background: `conic-gradient(transparent 0%, ${soft} 4%, transparent 12%, transparent 50%, ${highlight} 56%, transparent 62%)`,
            opacity: 0.7,
          }}
        />
      </div>
    </>
  );
}
